import { SettingsUsersForm } from "../../../models/pages/settings/users.model";
import { ProviderDetail } from "../../../models/entities/provider.model";
import { Institution } from "../../../models/entities/institution.model";
import { Email } from "../../../models/entities/email.model";
import { Phone } from "../../../models/entities/phone.model";
import { Formatter } from "../../../services/formatter/formatter.service";

/**
 * Builds a PATCH request body by comparing form data to the entity.
 * Returns only the fields that have changed.
 *
 * The backend (GDR `CoachesService.updateCoach`) skips any key that is absent or null,
 * which is why falsy-but-present values (false, "") must be explicitly included.
 */
export const buildUserUpdateRequest = (
	formData: SettingsUsersForm,
	entity: ProviderDetail | null,
	institutions: Institution[]
) => {
	const result: { [x: string]: any } = {};
	const emails: Partial<Email>[] = [];

	/** `undefined`, `null` and `""` all mean "no value"; anything else (including `false` and `0`) is a value. */
	const isEmpty = (value: unknown) => value === undefined || value === null || value === "";

	/** True when the form value differs from the entity value, treating all empty representations as equal. */
	const hasChanged = (formValue: unknown, entityValue: unknown) =>
		isEmpty(formValue) && isEmpty(entityValue) ? false : formValue !== entityValue;

	for (const key in formData) {
		if (Object.hasOwn(formData, key)) {
			switch (key) {
				case "dob": {
					const entityDob = entity?.dob ? new Date(entity.dob).toISOString() : null;
					const formDob = formData.dob ? formData.dob.toISOString() : null;
					if (formDob !== entityDob) {
						result.dob = Formatter.dateUTCWithoutTime(formData.dob)?.toISOString();
					}
					break;
				}
				case "email1":
					const email1 = entity?.emails?.find((x) => x.isPrimary);
					if (email1 && formData.email1 && email1.emailAddress !== formData.email1) {
						// The email has to be updated
						emails.push({
							emailId: email1.emailId,
							emailAddress: formData.email1,
							isPrimary: true,
						});
					} else if (email1 && !formData.email1) {
						// The email has to be deleted
						emails.push({
							emailId: email1.emailId,
							deleted: true,
						});
					} else if (!email1 && formData.email1) {
						// The email has to be created
						emails.push({
							emailId: null,
							emailAddress: formData.email1,
							isPrimary: true,
						});
					}
					break;
				case "email2":
					// Email 2
					const email2 = entity?.emails?.find((x) => !x.isPrimary && !x.deleted);
					if (email2 && formData.email2 && email2.emailAddress !== formData.email2) {
						// The email has to be updated
						emails.push({
							emailId: email2.emailId,
							emailAddress: formData.email2,
							isPrimary: false,
						});
					} else if (email2 && !formData.email2) {
						// The email has to be deleted
						emails.push({
							emailId: email2.emailId,
							deleted: true,
						});
					} else if (!email2 && formData.email2) {
						// The email has to be created
						emails.push({
							emailId: null,
							emailAddress: formData.email2,
							isPrimary: false,
						});
					}

					break;
				case "phone1":
					// NOTE: clearing the phone to blank is still dropped (there is no phone-delete
					// path here the way there is for emails). Tracked separately, not part of KAN-2404.
					const phone1 = entity?.phones?.find((x) => x.isPrimary);
					if (formData.phone1 && phone1?.phoneNumber !== formData.phone1) {
						const phones: Partial<Phone>[] = [
							{
								phoneId: phone1?.phoneId || null,
								countryCode: "+1",
								phoneNumber: formData.phone1,
								isPrimary: true,
							},
						];
						// result.contactId = entity?..contactId;
						result.phones = phones;
					}
					break;
				case "institutionId":
					if (entity && formData.institutionId !== entity.institutionId) {
						result.institutionName = institutions.find(
							(x) => x.institutionId === formData.institutionId
						)?.institutionName;
					}
					break;
				default:
					// KAN-2404: this used to be a truthiness test, which silently dropped every
					// falsy change — unchecking "Actively coaching" (false) or clearing a text
					// field ("") never reached the request body, so the backend skipped it.
					if (
						hasChanged(
							formData[key as keyof SettingsUsersForm],
							entity?.[key as keyof ProviderDetail]
						)
					) {
						result[key as keyof SettingsUsersForm] =
							formData[key as keyof SettingsUsersForm];
					}
					break;
			}
		}
	}

	if (emails.length > 0) {
		result.emails = emails;
	}

	return result;
};
